import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import {
  get as getFromLocalStorage,
  set as setToLocalStorage,
} from 'local-storage';

import { THEME_NAMES } from '@polyai/ui/styles/config/theme.constants';

import { closeSidePanel, projectInit } from 'actions';

export type SidePanelType =
  | 'node'
  | 'audio-testing'
  | 'flow-function-edit'
  | 'draft-change-log'
  | 'conversation-summary';
type SaveState = 'saving' | 'saved' | 'idle';

export const MIN_SIDE_PANEL_WIDTH = 415;

const THEME_STORAGE_KEY = 'polyai.theme';

/**
 * Detect system color scheme preference
 * @return {THEME_NAMES.NEW | THEME_NAMES.DARK} The detected theme preference
 */
export const getSystemThemePreference = ():
  | THEME_NAMES.NEW
  | THEME_NAMES.DARK => {
  if (typeof window === 'undefined') {
    return THEME_NAMES.NEW;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEME_NAMES.DARK
    : THEME_NAMES.NEW;
};

export const resolveTheme = (selectedTheme: THEME_NAMES): THEME_NAMES => {
  if (selectedTheme === THEME_NAMES.SYSTEM) {
    return getSystemThemePreference();
  }
  return selectedTheme;
};

const getInitialTheme = (): THEME_NAMES => {
  const stored = getFromLocalStorage(THEME_STORAGE_KEY) as THEME_NAMES | null;

  if (stored && Object.values(THEME_NAMES).includes(stored)) {
    return stored;
  }

  // Default to light mode for new users
  return THEME_NAMES.NEW;
};

export interface LayoutState {
  isActionHeaderOpen: boolean;
  audioWrapperHeight: number;
  sidePanelWidths: Record<SidePanelType, number>;
  sidePanelOpen: SidePanelType | null;
  saveState: SaveState;
  theme: THEME_NAMES;
  isNewDraftChanges: boolean;
  changeSidebarBranchId?: string;
  changeSidebarProjectId?: string;
  isAccountPanelExpanded: boolean;
}

const initialState: LayoutState = {
  isActionHeaderOpen: false,
  audioWrapperHeight: 0,
  sidePanelWidths: {
    node: getFromLocalStorage('polyai.sidePanelWidth') || MIN_SIDE_PANEL_WIDTH,
    'audio-testing': 650,
    'flow-function-edit': MIN_SIDE_PANEL_WIDTH,
    'draft-change-log': MIN_SIDE_PANEL_WIDTH,
    'conversation-summary': MIN_SIDE_PANEL_WIDTH,
  },
  sidePanelOpen: null,
  saveState: 'idle',
  theme: getInitialTheme(),
  isNewDraftChanges: false,
  isAccountPanelExpanded: false,
};

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    setAudioWrapperHeight: (
      state,
      { payload: height }: PayloadAction<number>,
    ) => {
      state.audioWrapperHeight = height;
    },
    openSidePanel: (state, { payload: id }: PayloadAction<SidePanelType>) => {
      state.sidePanelOpen = id;
      state.changeSidebarBranchId = undefined;
    },
    setSavedState: (
      state,
      { payload: saveState }: PayloadAction<SaveState>,
    ) => {
      state.saveState = saveState;
    },
    setSidePanelWidth: (
      state,
      {
        payload: { id, width },
      }: PayloadAction<{
        id: SidePanelType;
        width: number;
      }>,
    ) => {
      state.sidePanelWidths[id] = width;
    },
    setIsActionHeaderOpen: (
      state,
      { payload: open }: PayloadAction<boolean>,
    ) => {
      state.isActionHeaderOpen = open;
    },
    setTheme: (state, { payload: theme }: PayloadAction<THEME_NAMES>) => {
      state.theme = theme;
      setToLocalStorage(THEME_STORAGE_KEY, theme);
    },
    setIsNewDraftChanges: (
      state,
      { payload: isNewDraftChanges }: PayloadAction<boolean>,
    ) => {
      state.isNewDraftChanges = isNewDraftChanges;
    },
    setChangeSidebarBranchId: (
      state,
      {
        payload: { projectId, branchId },
      }: PayloadAction<{ branchId: string | undefined; projectId?: string }>,
    ) => {
      if (branchId) {
        state.sidePanelOpen = 'draft-change-log';
      }
      state.changeSidebarProjectId = projectId;
      state.changeSidebarBranchId = branchId;
    },
    setIsAccountPanelExpanded: (
      state,
      { payload: isAccountPanelExpanded }: PayloadAction<boolean>,
    ) => {
      state.isAccountPanelExpanded = isAccountPanelExpanded;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(closeSidePanel, state => {
        state.sidePanelOpen = null;
      })
      .addCase(projectInit, state => {
        return { ...initialState, theme: state.theme };
      });
  },
});

export const {
  setAudioWrapperHeight,
  setSidePanelWidth,
  openSidePanel,
  setSavedState,
  setIsActionHeaderOpen,
  setTheme,
  setIsNewDraftChanges,
  setChangeSidebarBranchId,
  setIsAccountPanelExpanded,
} = layoutSlice.actions;

export default layoutSlice.reducer;
